// ============================================================================
// Aptitude Analytics — shared, presentation-only helpers.
//
// The Aptitude module is a CONTINUOUS performance tracker (many attempts over
// time), unlike the Diagnostic module (a one-time baseline). Everything here is
// display/derivation logic over the ALREADY-STORED per-attempt metrics
// (aptitude_attempt_scores) and the rolling profile (aptitude_profile).
// No metric is ever recalculated — we only read, format, and diff stored values.
// ============================================================================

// Aptitude identity palette — deliberately distinct from the Diagnostic report's
// flat navy/slate. A violet→indigo→cyan "growth journey" language with emerald
// for improvement and rose for decline.
export const APT = {
  primary: '#6d28d9',   // violet
  primaryDark: '#4c1d95',
  indigo: '#4f46e5',
  cyan: '#0891b2',
  up: '#059669',        // emerald — improved
  down: '#e11d48',      // rose — declined
  flat: '#64748b',      // slate — no change
  ink: '#0f172a',
  muted: '#64748b',
};

// Traffic-light colour for a 0–100 score (shared visual grammar with the rest
// of the app, so scores stay readable).
export const scoreColor = (score) => {
  const s = Number(score) || 0;
  if (s >= 80) return '#059669';
  if (s >= 50) return '#d97706';
  return '#e11d48';
};

export const num = (v) => (v == null || v === '' ? null : Number(v));
export const pct = (v) => (num(v) == null ? '—' : `${Math.round(num(v))}%`);

// The scientific metrics that are comparable attempt-to-attempt. Each maps to a
// stored scalar column on an aptitude_attempt_scores row. All are "higher = better".
export const COMPARABLE_METRICS = [
  { key: 'overall_score',   label: 'Knowledge',  short: 'KNW', icon: '🧠' },
  { key: 'behaviour_score', label: 'Behaviour',  short: 'BHV', icon: '🎯' },
  { key: 'eri_score',       label: 'ERI',        short: 'ERI', icon: '⚡' },
  { key: 'tpsi_score',      label: 'TPSI',       short: 'TPSI', icon: '⏱️' },
  { key: 'cer_score',       label: 'CER',        short: 'CER', icon: '⚙️' },
  { key: 'rci_score',       label: 'RCI',        short: 'RCI', icon: '🧭' },
  { key: 'ffi_score',       label: 'FFI',        short: 'FFI', icon: '🔋' },
];

// A change is only "improved"/"declined" past a small threshold; tiny wobble is
// treated as "no change" so the UI does not over-report noise.
export const CHANGE_EPS = 1.0;

export const changeDirection = (from, to) => {
  const a = num(from), b = num(to);
  if (a == null || b == null) return 'na';
  const d = b - a;
  if (d > CHANGE_EPS) return 'up';
  if (d < -CHANGE_EPS) return 'down';
  return 'flat';
};

export const directionMeta = (dir) => ({
  up:   { label: 'Improved',  color: APT.up,   arrow: '▲', sign: '+' },
  down: { label: 'Declined',  color: APT.down, arrow: '▼', sign: '' },
  flat: { label: 'No Change', color: APT.flat, arrow: '▬', sign: '' },
  na:   { label: '—',         color: APT.flat, arrow: '',  sign: '' },
}[dir] || { label: '—', color: APT.flat, arrow: '', sign: '' });

// Attempt label used across selectors, charts and cards.
export const attemptLabel = (row) => `Test ${row?.attempt_number ?? '?'}`;
export const attemptSubLabel = (row) =>
  row?.test_name || (row?.submitted_at ? new Date(row.submitted_at).toLocaleDateString() : '');

export const fmtDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  return isNaN(dt) ? '—' : dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

// Overall trend across a numeric series (first→last, robust to nulls).
// Uses the last few points to avoid a single early dip dominating the verdict.
export const trendVerdict = (series) => {
  const s = (series || []).map(num).filter((v) => v != null);
  if (s.length < 2) return { label: 'New', color: APT.flat, icon: '•' };
  const window = s.slice(-Math.min(4, s.length));
  const delta = window[window.length - 1] - window[0];
  if (delta > CHANGE_EPS) return { label: 'Improving', color: APT.up, icon: '↗' };
  if (delta < -CHANGE_EPS) return { label: 'Declining', color: APT.down, icon: '↘' };
  return { label: 'Stable', color: APT.flat, icon: '→' };
};

// Longest run of consecutive attempt-over-attempt improvements in a series.
export const longestImprovementStreak = (series) => {
  const s = (series || []).map(num).filter((v) => v != null);
  let best = 0, cur = 0;
  for (let i = 1; i < s.length; i++) {
    if (s[i] - s[i - 1] > CHANGE_EPS) { cur += 1; best = Math.max(best, cur); }
    else cur = 0;
  }
  return best;
};

// Biggest single attempt-to-attempt jump in a series → { gain, at }.
export const biggestJump = (rows, key) => {
  let best = null;
  for (let i = 1; i < rows.length; i++) {
    const a = num(rows[i - 1]?.[key]);
    const b = num(rows[i]?.[key]);
    if (a == null || b == null) continue;
    const gain = b - a;
    if (best == null || gain > best.gain) {
      best = { gain, from: rows[i - 1], to: rows[i] };
    }
  }
  return best;
};

// Milestone insights over the full history (Feature 3 header cards).
export const buildMilestones = (history, profile) => {
  const rows = history || [];
  const eri = rows.map((r) => num(r.eri_score)).filter((v) => v != null);
  const know = rows.map((r) => num(r.overall_score)).filter((v) => v != null);
  const jump = biggestJump(rows, 'eri_score');
  const bestEriRow = rows.reduce((best, r) =>
    (num(r.eri_score) != null && (best == null || num(r.eri_score) > num(best.eri_score))) ? r : best, null);
  const worstEriRow = rows.reduce((worst, r) =>
    (num(r.eri_score) != null && (worst == null || num(r.eri_score) < num(worst.eri_score))) ? r : worst, null);
  const bestKnowRow = rows.reduce((best, r) =>
    (num(r.overall_score) != null && (best == null || num(r.overall_score) > num(best.overall_score))) ? r : best, null);

  return {
    totalAttempts: profile?.total_attempts ?? rows.length,
    bestEri: eri.length ? Math.max(...eri) : null,
    bestEriRow,
    lowestEri: eri.length ? Math.min(...eri) : null,
    worstEriRow,
    highestKnowledge: know.length ? Math.max(...know) : null,
    bestKnowRow,
    biggestJump: jump,
    streak: longestImprovementStreak(eri),
    trend: trendVerdict(eri),
  };
};

// Build the {TPSI:{score}, ...} shape the BehaviourInsights component expects
// from a stored attempt row (scalar columns) — no recompute.
export const metricsFromRow = (row) => row ? {
  TPSI: { score: row.tpsi_score },
  CER:  { score: row.cer_score },
  RCI:  { score: row.rci_score },
  FFI:  { score: row.ffi_score },
} : null;

// Build the same shape from the rolling profile (Overview tab).
export const metricsFromProfile = (p) => p ? {
  TPSI: { score: p.tpsi_score },
  CER:  { score: p.cer_score },
  RCI:  { score: p.rci_score },
  FFI:  { score: p.ffi_score },
} : null;

export const readinessMeta = (level) => ({
  top_performer:     { label: 'Top Performer',     color: '#059669' },
  placement_ready:   { label: 'Placement Ready',   color: '#4f46e5' },
  moderate:          { label: 'Moderate',          color: '#d97706' },
  needs_improvement: { label: 'Needs Improvement', color: '#e11d48' },
}[level] || { label: 'Not Evaluated', color: '#64748b' });
